import type { JSX } from 'react'
import {
  Fragment,
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'

import { Button, DatePicker, Skeleton } from 'antd'
import classNames from 'classnames'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'

import { ReactComponent as PrevIcon } from 'assets/icons/left.svg'
import { ReactComponent as NextIcon } from 'assets/icons/right.svg'

import { BOOKING_STATUS } from 'common/constants/calendarConstants'
import { DATE_FORMAT } from 'common/constants/dateFormatConstants'

import type { INewLeaseDays } from 'features/Booking/interfaces/ILeaseInfoRoom'
import './calendar.scss'

interface IProps {
  days?: INewLeaseDays
  currentDay?: string
  showCurrentDay?: boolean
  activeDays?: string[]
  selectedDays?: string[]
  isStrictDisable?: boolean
  withFakeRerender?: boolean
  isLoading?: boolean
  disabledCells?: boolean
  minDate?: Dayjs
  maxDate?: Dayjs
  triggerInitialEffect?: boolean
  needConfirm?: boolean
  /** When true, every calendar cell click calls handleChangeDate (e.g. multi-day toggle on same cell). */
  emitRepeatDateChange?: boolean
  allowClickDisabledDate?: boolean
  handleChangeDate?: (date: string) => void
  handleChangeMonth?: (id?: number | null, date?: string) => void
  onPrevButtonClick?: () => void
  onNextButtonClick?: () => void
  onOpenChange?: (open: boolean) => void
}

export interface CalendarRef {
  getCurrentDate: () => Dayjs | null
}

export const Calendar = memo(
  forwardRef<CalendarRef, IProps>(
    (
      {
        days = {},
        currentDay,
        selectedDays = [],
        activeDays = [],
        isLoading = false,
        isStrictDisable = false,
        withFakeRerender = false,
        disabledCells = false,
        showCurrentDay = true,
        minDate,
        maxDate,
        triggerInitialEffect = true,
        needConfirm = false,
        emitRepeatDateChange = false,
        allowClickDisabledDate = true,
        handleChangeDate = () => undefined,
        handleChangeMonth = () => undefined,
        onPrevButtonClick = () => undefined,
        onNextButtonClick = () => undefined,
        onOpenChange = () => undefined,
      },
      ref,
    ) => {
      const [currentViewMonth, setCurrentViewMonth] = useState<Dayjs | null>(null)

      useEffect(() => {
        if (currentDay && !currentViewMonth) {
          setCurrentViewMonth(dayjs(currentDay))
        }
      }, [currentDay, currentViewMonth])

      useImperativeHandle(ref, () => ({
        getCurrentDate: () => {
          return currentViewMonth
        },
      }))

      useEffect(() => {
        const todayPickerCell = document.querySelector('.ant-picker-cell-today')
        if (todayPickerCell) {
          todayPickerCell.classList.remove('ant-picker-cell-today')
        }

        const formatDate = (dateString: string) => {
          const [year, month, day] = dateString.split('-')
          return `${month}/${day}/${year}`
        }

        const tdElements = document.querySelectorAll('.ant-picker-content td[title]')

        if (!tdElements.length || tdElements[0].getAttribute('title')?.includes('/')) {
          return
        }

        tdElements.forEach((td) => {
          const originalTitle = td.getAttribute('title')
          const formattedTitle = formatDate(originalTitle)
          td.setAttribute('title', formattedTitle)
        })
      })

      const getBookingStatus = (date: Dayjs): string => {
        const dayData = days[date.format(DATE_FORMAT.PATIENT_TRACKER_DATE)]
        if (!dayData || dayData.active !== 1) return ''

        switch (dayData.occupancy_type) {
          case BOOKING_STATUS.EMPTY:
            return `booked-${BOOKING_STATUS.EMPTY}`
          case BOOKING_STATUS.QUARTER:
            return `booked-${BOOKING_STATUS.QUARTER}`
          case BOOKING_STATUS.HALF:
            return `booked-${BOOKING_STATUS.HALF}`
          case BOOKING_STATUS.THREE_QUARTERS:
            return `booked-${BOOKING_STATUS.THREE_QUARTERS}`
          case BOOKING_STATUS.FULL:
            return `booked-${BOOKING_STATUS.FULL}`
          default:
            return ''
        }
      }

      const selectedDate = useMemo(() => {
        if (!days) return

        const tomorrow: string = dayjs().add(1, 'day').format(DATE_FORMAT.PATIENT_TRACKER_DATE)
        let nextActiveDate: Dayjs | null = null

        for (
          let date: string = tomorrow;
          date in days;
          date = dayjs(date).add(1, 'day').format(DATE_FORMAT.PATIENT_TRACKER_DATE)
        ) {
          if (days[date].active === 1) {
            nextActiveDate = dayjs(date)
            break
          }
        }

        return nextActiveDate
      }, [days])

      const onChangePickerDate = (date: string): void => {
        if (needConfirm) setCurrentViewMonth(dayjs(date))
        if (emitRepeatDateChange || currentDay !== date) handleChangeDate(date)
      }

      const dateRender = (date: Dayjs): React.JSX.Element => {
        const isFormattedDate = date.format(DATE_FORMAT.PATIENT_TRACKER_DATE)
        const bookingStatus = getBookingStatus(date)

        const isActiveDay = activeDays.some((day) => dayjs(day).isSame(date, 'day'))

        const isCurrentActiveDay =
          currentDay === date.format(DATE_FORMAT.PATIENT_TRACKER_DATE) && showCurrentDay

        return (
          <Fragment key={date.format(DATE_FORMAT.PATIENT_TRACKER_DATE)}>
            <div className={classNames({ none: !isLoading })}>
              <Skeleton.Avatar active size='large' shape='circle' />
            </div>
            <div className={classNames({ none: isLoading })}>
              <div
                onClick={() => {
                  if (!isDisableDate(date) || allowClickDisabledDate) {
                    onChangePickerDate(isFormattedDate)
                  }
                }}
                className={classNames('ant-picker-cell-inner', {
                  activeDay: selectedDays.includes(isFormattedDate),
                  'booking-availability': isFormattedDate,
                  day: isFormattedDate,
                  [bookingStatus]: isFormattedDate,
                  currentActiveDay: isCurrentActiveDay || isActiveDay,
                })}>
                {date.date()}
              </div>
            </div>
          </Fragment>
        )
      }

      const isDisableDate = (date: Dayjs): boolean => {
        if (disabledCells) return true

        if (!Object.keys(days).length) return true

        const isInvalidCurrentDate = !Boolean(days[date.format(DATE_FORMAT.PATIENT_TRACKER_DATE)])
        const isNotActiveCurrentDate =
          days[date.format(DATE_FORMAT.PATIENT_TRACKER_DATE)]?.active === 0

        if (
          (isStrictDisable && isInvalidCurrentDate) ||
          // !checkActiveStatus(date) ||
          isNotActiveCurrentDate
        ) {
          return true
        } else if (isStrictDisable && !isInvalidCurrentDate) {
          return false
        }

        // if (date.isBefore(dayjs().startOf('month'))) {
        //   return true
        // }

        return false
      }

      useEffect(() => {
        if (triggerInitialEffect) {
          handleChangeDate(currentDay)
        }
      }, [])

      return (
        <div className='calendar'>
          <DatePicker
            needConfirm={needConfirm}
            open
            autoFocus
            dateRender={dateRender}
            minDate={minDate}
            maxDate={maxDate}
            value={withFakeRerender ? null : currentViewMonth || selectedDate}
            onPanelChange={(date: Dayjs, type: string) => {
              if (type === 'date') {
                setCurrentViewMonth(date)
                handleChangeMonth(null, date.format('YYYY-MM'))
              }
            }}
            onOpenChange={onOpenChange}
            disabledDate={isDisableDate}
            prevIcon={<Button icon={<PrevIcon />} onClick={onPrevButtonClick} />}
            nextIcon={<Button icon={<NextIcon />} onClick={onNextButtonClick} />}
            getPopupContainer={(trigger: any) => trigger.parentNode}
          />
        </div>
      )
    },
  ),
)
